import { memo } from "react";
import styled from "styled-components";

import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import { ReactComponent as CheckIcon } from "../assets/check.svg";

import Card from "./Cards/Card";
import IconButton from "./Buttons/IconButton";
import type {
  InspectionFieldCard$key,
  InspectionfieldStateEnum,
} from "./__generated__/InspectionFieldCard.graphql";
import { formatDateShort } from "../utils";
import FieldState from "./InspectionField.state";

export enum FieldCardButtons {
  OnClick,
  OnClickFocus,
  OnClickNavigate,
  OnClickPlan,
  OnClickRetry,
  OnClickSelect,
}

export interface Props {
  field: InspectionFieldCard$key;
  selected?: boolean;
  focused?: boolean;
  map?: boolean;
  disabledButtons?: FieldCardButtons[];
  onClick?: (id: string) => void;
  onClickFocus?: (id: string) => void;
  onClickNavigate?: (id: string) => void;
  onClickPlan?: (id: string) => void;
  onClickRetry?: (id: string) => void;
  onClickSelect?: (id: string) => void;
}

const InspectionFieldCardFragment = graphql`
  fragment InspectionFieldCard on InspectionfieldNode {
    id
    externalId
    state
    year
    periodStart
    periodEnd
    assignedDate
    plannedDate
    latestInspection {
      retryDate
      approvedDate
      rejectedDate
      lastModified
    }
    managementPackageSetting {
      displayNameAlt
    }
    member {
      name
    }
    field {
      id
      previousId
    }
  }
`;

export default memo(function Comp(props: Props) {
  const data = useFragment(InspectionFieldCardFragment, props.field);
  return (
    <InspectionFieldCard
      id={`${data.externalId}`}
      externalId={`${data.externalId}`}
      field={data.field}
      managementPackageSettingName={
        data?.managementPackageSetting?.displayNameAlt || ""
      }
      member={data?.member?.name}
      periodStart={data?.periodStart}
      periodEnd={data?.periodEnd}
      assignedDate={data?.assignedDate}
      plannedDate={data.plannedDate}
      lastModified={data?.latestInspection?.lastModified}
      inspectionDate={
        data?.latestInspection?.approvedDate ||
        data?.latestInspection?.rejectedDate ||
        data?.latestInspection?.retryDate
      }
      state={data?.state!}
      onClickRetry={
        props?.onClickRetry
          ? () => props.onClickRetry && props.onClickRetry(data.id)
          : undefined
      }
      focused={props.focused}
      selected={data?.state === "ASSIGNED" && props.selected}
      disabledButtons={props.disabledButtons}
      onClick={() => props.onClick && props.onClick(data.id)}
      onClickSelect={
        props?.onClickSelect
          ? () =>
              props.onClickSelect &&
              data?.state === "ASSIGNED" &&
              props.onClickSelect(data.id)
          : undefined
      }
      onClickPlan={
        props.onClickPlan
          ? () => props.onClickPlan && props.onClickPlan(data.id)
          : undefined
      }
      onClickFocus={
        props.onClickFocus
          ? () => props.onClickFocus && props.onClickFocus(data.id)
          : undefined
      }
      onClickNavigate={
        props.onClickNavigate
          ? () => props.onClickNavigate && props.onClickNavigate(data.id)
          : undefined
      }
      year={data.year}
      map={Boolean(props?.map)}
    />
  );
});

/**
UI components
**/
export interface CardProps {
  id: string;
  externalId?: string;
  managementPackageSettingName?: string;
  member?: string;
  field?: any;
  state?: InspectionfieldStateEnum;
  periodStart?: string;
  periodEnd?: string;
  assignedDate?: string;
  plannedDate?: string;
  inspectionDate?: string;
  lastModified?: string;
  focused?: boolean;
  selected?: boolean;
  disabledButtons?: FieldCardButtons[];
  onClick?: Function;
  onClickFocus?: Function;
  onClickNavigate?: Function;
  onClickPlan?: Function;
  onClickRetry?: React.MouseEventHandler<HTMLButtonElement>;
  onClickSelect?: Function;
  year?: number;
  map?: boolean;
}
export const InspectionFieldCard = memo(function InspectionPFCard(
  props: CardProps
) {
  const handleSelect: any = (event: any) => {
    if (
      !props.disabledButtons?.some(
        (disabled) => disabled === FieldCardButtons.OnClickSelect
      ) &&
      props.onClickSelect
    ) {
      props.onClickSelect();
    } else if (
      !props.disabledButtons?.some(
        (disabled) => disabled === FieldCardButtons.OnClick
      ) &&
      props.onClick
    ) {
      props.onClick();
    }
    event.stopPropagation();
    return false;
  };
  return (
    <Container
      as={Card}
      onClick={
        props.disabledButtons?.some(
          (disabled) => disabled === FieldCardButtons.OnClick
        )
          ? undefined
          : props.onClick
      }
      {...(props.focused ? { "data-focused": true } : {})}
    >
      <Header onClick={handleSelect}>
        <HeaderName>
          {props.onClickSelect && props.state === "ASSIGNED" && (
            <FieldSelect selected={props.selected} onClick={handleSelect} />
          )}
          <Title
            style={{
              textDecoration: `${props.field ? "none" : "line-through"}`,
            }}
          >
            BE {props.externalId}
          </Title>
        </HeaderName>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <CardYear>
            <CardYearText>{props.year}</CardYearText>
          </CardYear>
          {props.map && props.state ? (
            <FieldState pin state={props.state} onClick={props.onClickFocus} />
          ) : null}
        </div>
      </Header>
      <Attributes>
        {props?.managementPackageSettingName && (
          <>
            <TextAttribute text={props.managementPackageSettingName} />
            <AttributesDivider />
          </>
        )}
        {props?.member && (
          <>
            <TextAttribute text={props?.member} />
          </>
        )}
        {!isOpen(props.state) && props.inspectionDate ? (
          <>
            <AttributesDivider />
            <Attribute>{`Geschouwd op ${formatDateShort(
              props.inspectionDate
            )}`}</Attribute>
          </>
        ) : props.periodStart || props.periodEnd ? (
          <>
            <AttributesDivider />
            <FieldPeriod
              periodStart={props.periodStart}
              periodEnd={props.periodEnd}
            />
          </>
        ) : null}
        {props.assignedDate ? (
          <>
            <AttributesDivider />
            <Attribute>{`Toegewezen op ${formatDateShort(
              props.assignedDate
            )}`}</Attribute>
          </>
        ) : null}
      </Attributes>
      {(props.inspectionDate || props.plannedDate) && <FooterDivider />}
      <Footer>
        <div>
          {props.state && (
            <FieldState
              state={props.state}
              text={props.lastModified || props.plannedDate}
            />
          )}
          {props.state && props.inspectionDate && isOpen(props.state) && (
            <FieldState state="RETRY" />
          )}
        </div>
        <FooterRight>
          {props.onClickFocus && (
            <IconButton
              icon="target"
              tooltip="Bekijk op kaart"
              disabled={props.disabledButtons?.some(
                (disabled) => disabled === FieldCardButtons.OnClickFocus
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onClickFocus && props.onClickFocus(e);
              }}
            />
          )}
          {props.onClickRetry && props.state === "RETRY" && (
            <IconButton
              icon="repeat"
              tooltip="Opnieuw inplannen"
              disabled={props.disabledButtons?.some(
                (disabled) => disabled === FieldCardButtons.OnClickRetry
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onClickRetry && props.onClickRetry(e);
              }}
            />
          )}
          {props.onClickPlan &&
          ["ASSIGNED", "PLANNED"]?.includes(props.state || "") ? (
            <IconButton
              icon={"calendar"}
              backgroundColor={props.state === "ASSIGNED" ? "green" : undefined}
              iconColor={props.state === "ASSIGNED" ? "white" : undefined}
              tooltip={
                props.state === "PLANNED"
                  ? "Wijzig ingeplande datum"
                  : "Schouw inplannen"
              }
              disabled={props.disabledButtons?.some(
                (disabled) => disabled === FieldCardButtons.OnClickPlan
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onClickPlan && props.onClickPlan(props.id ?? "");
              }}
            />
          ) : null}
          {props.onClickNavigate && (
            <IconButton
              icon="arrow-right"
              backgroundColor="green"
              tooltip="Navigeer naar BE"
              disabled={props.disabledButtons?.some(
                (disabled) => disabled === FieldCardButtons.OnClickNavigate
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onClickNavigate && props.onClickNavigate(e);
              }}
            />
          )}
        </FooterRight>
      </Footer>
    </Container>
  );
});

/**
Utils
**/
const isOpen = (state?: InspectionfieldStateEnum) => {
  return (
    state &&
    (
      [
        "OPEN",
        "ASSIGNED",
        "PLANNED",
        "IN_PROGRESS",
      ] as InspectionfieldStateEnum[]
    ).includes(state)
  );
};

const Container = styled.div<any>`
  cursor: ${(props) => (props.onClick ? "pointer" : "select")};
  width: 340px;
  min-height: 134px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 10px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  border: 4px solid transparent;

  background-color: ${(props) =>
    props["data-focused"] ? "lightyellow" : "white"};

  @media only screen and (max-width: 768px) {
    width: 256px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
  justify-content: space-between;
`;

const HeaderName = styled.div`
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
`;

const Title = styled.div`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  &.done {
    opacity: 0.5;
  }
  padding-top: 12px;
  padding-bottom: 12px;
`;

const CardYear = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 5px;
  border: 1px solid grey;
  display: flex;
  width: 57px;
  height: 22px;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
`;

const CardYearText = styled.span`
  color: var(--Donkergrijs, #212529);
  text-align: center;
  font-family: Roboto;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 15.6px */
`;

const Checkbox = styled.button`
  cursor: pointer;
  background: ${(props) => (props.color === "blue" ? "#0075BC" : "#FAFAFA")};
  border: 1px solid
    ${(props) =>
      props.color === "blue" ? "rgba(0, 117, 188, 0.4)" : "#E5E5E5"};
  border-radius: 2px;
  text-align: center;
  align-items: center;
  width: 25px;
  height: 25px;
  margin-right: 14px;
`;

const Attributes = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  text-transform: uppercase;
`;

const AttributesDivider = styled.div`
  width: 3px;
  height: 3px;
  background-color: #212529;
  margin-left: 8px;
  margin-right: 8px;
  align-content: center;
`;

const Attribute = styled.div`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  font-size: 10px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  min-height: 30px;
`;

const FooterRight = styled.div`
  display: flex;
  flex-direction: row;
  align-self: flex-end;
  gap: 4px;
`;

const FooterDivider = styled.div`
  width: 100%;
  height: 0px;
  border: 1px solid #e5e5e5;
  margin-top: 4px;
  margin-bottom: 4px;
`;

function FieldSelect({
  selected,
  onClick,
}: {
  selected?: boolean;
  onClick?: any;
}) {
  return (
    <Checkbox color={selected ? "blue" : "white"} onClick={onClick}>
      {selected && <CheckIcon fill="white" />}
    </Checkbox>
  );
}

function TextAttribute({ text }: { text?: string }) {
  if (!text) return null;
  return <Attribute>{text}</Attribute>;
}

function FieldPeriod({
  periodStart,
  periodEnd,
}: {
  periodStart?: string;
  periodEnd?: string;
}) {
  return (
    <Attribute>
      Periode:&nbsp;
      {periodStart && formatDateShort(periodStart)}
      {periodStart && periodEnd ? <> - </> : null}
      {periodEnd && formatDateShort(periodEnd)}
    </Attribute>
  );
}
